import PropTypes from "prop-types"
import { Modal, Flex, Tooltip } from "antd"
import { DSIcon } from "ui/Icon/DSIcon"
import { Text } from "ui/Typography"
import CustomDrawer from "ui/Drawer"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { GUEST_STATE, guestMissingDocuments } from "../checkInUtils"
import PaymentTag from "../../PaymentTag"
import styles from "./CheckInGuestsModal.module.css"

// Per-guest IN/OUT toggle. pending → check-in artwork (grey) · in → check-in (green) ·
// out → check-out (green). Clicking cycles the guest through the flow.
const GuestToggle = ({ state, onClick }) => {
  const isOut = state === GUEST_STATE.OUT
  const isIn = state === GUEST_STATE.IN
  const icon = isOut ? (
    <DSIcon name="checkOut" variant="active" width={52} height={25} />
  ) : (
    <DSIcon name="checkIn" variant={isIn ? "active" : "pending"} width={52} height={25} />
  )
  let tip = "Click to check-in"
  if (isOut) tip = "Checked out, click to reset"
  else if (isIn) tip = "Checked-in, click to check out"
  return (
    <Tooltip title={tip}>
      <button
        type="button"
        className={`${styles.guestToggle} ${state === GUEST_STATE.PENDING ? styles.guestTogglePending : ""}`}
        onClick={onClick}
      >
        {icon}
      </button>
    </Tooltip>
  )
}

GuestToggle.propTypes = {
  state: PropTypes.oneOf(["pending", "in", "out"]).isRequired,
  onClick: PropTypes.func
}

// Group check-in/out modal: one row per booking with an IN/OUT toggle for the whole group
// (client name + guest count), booking-level document status, and Resend links for each
// missing document. Private bookings are checked in/out as one unit, not per individual.
const CheckInGuestsModal = ({
  open,
  onClose,
  bookingId,
  clientName,
  paymentTag,
  collectionTag,
  guests = [],
  isResending = false,
  onToggleGuest,
  onResend
}) => {
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)

  // Counts are guest-weighted (each row carries its group's guestCount) so the header reflects
  // people, not rows.
  const totalGuests = guests.reduce((sum, guest) => sum + (guest.guestCount ?? 1), 0)
  const checkedInGuests = guests.reduce((sum, guest) => sum + (guest.state === GUEST_STATE.IN ? guest.guestCount ?? 1 : 0), 0)

  const content = (
    <>
      <Flex vertical gap={4} className={styles.metaRow}>
        <Flex align="center" gap={8} wrap="wrap">
          <Text variant="caption" size="md" className={styles.bookingId}>BOOKING ID: {bookingId}</Text>
          {paymentTag?.label && <PaymentTag label={paymentTag.label} color={paymentTag.color} />}
          {collectionTag?.label && <PaymentTag label={collectionTag.label} color={collectionTag.color} />}
        </Flex>
        <Flex align="center" justify="space-between" gap={8}>
          <Text variant="body" size="md" fw={600} color="var(--color-text-primary)" className={styles.clientName}>{clientName}</Text>
          <Text variant="body" size="md" color="var(--color-text-primary)" className={styles.checkedIn}>
            Checked-in: <strong className={styles.checkedInCount}>{checkedInGuests}/{totalGuests}</strong>
          </Text>
        </Flex>
      </Flex>

      <Flex vertical className={styles.guestList}>
        {guests.map((guest) => {
          const missing = guestMissingDocuments(guest)
          const docsComplete = (guest.docsTotal ?? 0) === 0 || (guest.docsSigned ?? 0) >= (guest.docsTotal ?? 0)
          const guestCountLabel = guest.guestCount ? `(${guest.guestCount} ${guest.guestCount === 1 ? "guest" : "guests"})` : null
          return (
            <div key={guest.id} className={styles.guestBlock}>
              <Flex align="center" gap={12} className={styles.guestRow}>
                <GuestToggle state={guest.state} onClick={() => onToggleGuest?.(guest)} />
                <span className={styles.guestName}>{guest.name}</span>
                <Flex align="center" gap={16} flex={1} className={styles.guestMetaWrap}>
                  {guestCountLabel && <span className={styles.guestMeta}>{guestCountLabel}</span>}
                  {guest.phone && <span className={styles.guestMeta}>{guest.phone}</span>}
                  {guest.type && <span className={styles.guestType}>{guest.type}</span>}
                </Flex>
                <span className={`${styles.docsBadge} ${docsComplete ? styles.docsComplete : styles.docsIncomplete}`}>
                  <DSIcon name="file" width={14} height={14} fill="currentColor" />
                  {guest.docsSigned}/{guest.docsTotal}
                </span>
              </Flex>

              {missing.length > 0 && (
                <Flex vertical gap={6} className={styles.missingList}>
                  {missing.map((doc) => (
                    <Flex key={doc.id} align="center" justify="space-between" className={styles.missingRow}>
                      <Flex align="center" gap={6}>
                        <DSIcon name="file" width={14} height={14} fill="currentColor" />
                        <span className={styles.missingTitle}>{doc.title}</span>
                      </Flex>
                      <Flex align="center" gap={16}>
                        <span className={styles.missingTag}>Missing</span>
                        <button type="button" className={styles.resendBtn} onClick={() => onResend?.(guest, doc)} disabled={isResending}>
                          Resend
                        </button>
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              )}
            </div>
          )
        })}
      </Flex>
    </>
  )

  // Mobile: slide up as a bottom sheet (matches the app's other Bookings modals); desktop: centered modal.
  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={(next) => !next && onClose?.()} title="Check-in/out Guests">
        {content}
      </CustomDrawer>
    )
  }

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={560} centered title="Check-in/out Guests">
      {content}
    </Modal>
  )
}

CheckInGuestsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientName: PropTypes.string,
  paymentTag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
  collectionTag: PropTypes.shape({ label: PropTypes.string, color: PropTypes.string }),
  guests: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      guestCount: PropTypes.number,
      phone: PropTypes.string,
      type: PropTypes.string,
      state: PropTypes.oneOf(["pending", "in", "out"]),
      docsSigned: PropTypes.number,
      docsTotal: PropTypes.number,
      documents: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.string, title: PropTypes.string, isSigned: PropTypes.bool })
      )
    })
  ),
  isResending: PropTypes.bool,
  onToggleGuest: PropTypes.func,
  onResend: PropTypes.func
}

export default CheckInGuestsModal
